/**
 * Multi-region configuration.
 *
 * Nako Pula started as a Botswana-only product. As we expand into South
 * Africa, Nigeria, and Zimbabwe the accounting engine swaps tax rules,
 * currency, PAYE brackets, and corporate-tax rates per tenant.
 *
 * Sources (May 2026):
 *   - Botswana:     BURS PAYE schedule, Income Tax Act
 *   - South Africa: SARS, EMP201 / EMP501
 *   - Nigeria:      FIRS, Finance Act 2023; e-invoicing mandate from 2026
 *   - Zimbabwe:     ZIMRA 2025/2026 tax tables (USD), Finance Act 2025
 */
export type RegionCode = 'BW' | 'ZA' | 'NG' | 'ZW';

export interface PayeBracket {
  threshold: number;
  rate: number;
  cumulative: number;
}

export interface PayrollLevy {
  name: string;
  rate: number;
  employeeShare: number;
  employerShare: number;
  monthlyCeiling?: number;
}

export interface WithholdingTax {
  category: string;
  rate: number;
}

export interface RegionConfig {
  code: RegionCode;
  displayName: string;
  currencyCode: string;
  currencySymbol: string;
  locale: string;
  vat: {
    rate: number;
    registrationThreshold: number;
    returnForm: string;
  };
  corporateTax: {
    standard: number;
    small?: number;
    smallTurnoverCeiling?: number;
    manufacturing?: number;
  };
  paye: PayeBracket[];
  payrollLevies: PayrollLevy[];
  withholding: WithholdingTax[];
  languages: Array<{ code: string; name: string; nativeName: string }>;
  fiscalYearStartMonth: number;
  taxAuthority: string;
  blurb: string;
}

// ────────────────────────────────────────────────────────────────────
// Botswana — current home market
// ────────────────────────────────────────────────────────────────────
const BW: RegionConfig = {
  code: 'BW',
  displayName: 'Botswana',
  currencyCode: 'BWP',
  currencySymbol: 'P',
  locale: 'en-BW',
  vat: {
    rate: 0.14,
    registrationThreshold: 1_000_000,
    returnForm: 'VAT 002',
  },
  corporateTax: {
    standard: 0.22,
    manufacturing: 0.15,
  },
  paye: [
    { threshold: 48_000, rate: 0.0, cumulative: 0 },
    { threshold: 84_000, rate: 0.05, cumulative: 0 },
    { threshold: 120_000, rate: 0.125, cumulative: 1_800 },
    { threshold: 156_000, rate: 0.1875, cumulative: 6_300 },
    { threshold: Infinity, rate: 0.25, cumulative: 13_050 },
  ],
  payrollLevies: [],
  withholding: [],
  languages: [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'tn', name: 'Setswana', nativeName: 'Setswana' },
  ],
  fiscalYearStartMonth: 7,
  taxAuthority: 'BURS',
  blurb: 'BURS VAT 002, BWP, PAYE up to 25%.',
};

// ────────────────────────────────────────────────────────────────────
// South Africa
// ────────────────────────────────────────────────────────────────────
const ZA: RegionConfig = {
  code: 'ZA',
  displayName: 'South Africa',
  currencyCode: 'ZAR',
  currencySymbol: 'R',
  locale: 'en-ZA',
  vat: {
    rate: 0.15,
    registrationThreshold: 2_300_000,
    returnForm: 'VAT201',
  },
  corporateTax: {
    standard: 0.27,
    small: 0.0,
    smallTurnoverCeiling: 99_000,
  },
  paye: [
    { threshold: 237_100, rate: 0.18, cumulative: 0 },
    { threshold: 370_500, rate: 0.26, cumulative: 42_678 },
    { threshold: 512_800, rate: 0.31, cumulative: 77_362 },
    { threshold: 673_000, rate: 0.36, cumulative: 121_475 },
    { threshold: 857_900, rate: 0.39, cumulative: 179_147 },
    { threshold: 1_817_000, rate: 0.41, cumulative: 251_258 },
    { threshold: Infinity, rate: 0.45, cumulative: 644_489 },
  ],
  payrollLevies: [
    {
      name: 'UIF',
      rate: 0.02,
      employeeShare: 0.01,
      employerShare: 0.01,
      monthlyCeiling: 17_712,
    },
    {
      name: 'SDL',
      rate: 0.01,
      employeeShare: 0,
      employerShare: 0.01,
    },
  ],
  withholding: [],
  languages: [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'zu', name: 'isiZulu', nativeName: 'isiZulu' },
  ],
  fiscalYearStartMonth: 3,
  taxAuthority: 'SARS',
  blurb: 'SARS VAT201, ZAR, UIF + SDL, EMP201 monthly.',
};

// ────────────────────────────────────────────────────────────────────
// Nigeria
// ────────────────────────────────────────────────────────────────────
const NG: RegionConfig = {
  code: 'NG',
  displayName: 'Nigeria',
  currencyCode: 'NGN',
  currencySymbol: '₦',
  locale: 'en-NG',
  vat: {
    rate: 0.075,
    registrationThreshold: 25_000_000,
    returnForm: 'FIRS VAT',
  },
  corporateTax: {
    standard: 0.25,
    small: 0.0,
    smallTurnoverCeiling: 25_000_000,
  },
  paye: [
    { threshold: 300_000, rate: 0.07, cumulative: 0 },
    { threshold: 600_000, rate: 0.11, cumulative: 21_000 },
    { threshold: 1_100_000, rate: 0.15, cumulative: 54_000 },
    { threshold: 1_600_000, rate: 0.19, cumulative: 129_000 },
    { threshold: 3_200_000, rate: 0.21, cumulative: 224_000 },
    { threshold: Infinity, rate: 0.24, cumulative: 560_000 },
  ],
  payrollLevies: [],
  withholding: [
    { category: 'Contracts / consultancy', rate: 0.05 },
    { category: 'Rent / dividends', rate: 0.1 },
    { category: 'Interest (non-FI)', rate: 0.1 },
  ],
  languages: [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'ha', name: 'Hausa', nativeName: 'Hausa' },
    { code: 'pcm', name: 'Pidgin', nativeName: 'Naija Pidgin' },
  ],
  fiscalYearStartMonth: 1,
  taxAuthority: 'FIRS',
  blurb: 'FIRS VAT 7.5%, NGN, WHT 5–10%, e-invoicing from 2026.',
};

// ────────────────────────────────────────────────────────────────────
// Zimbabwe
// ────────────────────────────────────────────────────────────────────
const ZW: RegionConfig = {
  code: 'ZW',
  displayName: 'Zimbabwe',
  currencyCode: 'USD',
  currencySymbol: 'US$',
  locale: 'en-ZW',
  vat: {
    rate: 0.155,
    registrationThreshold: 25_000,
    returnForm: 'ZIMRA VAT 7',
  },
  corporateTax: {
    standard: 0.25,
    small: 0.0,
    smallTurnoverCeiling: 0,
  },
  // USD annual brackets (monthly × 12). Source: ZIMRA 2025/2026 tables.
  paye: [
    { threshold: 1_200, rate: 0.0, cumulative: 0 },
    { threshold: 3_600, rate: 0.2, cumulative: 0 },
    { threshold: 12_000, rate: 0.25, cumulative: 480 },
    { threshold: 24_000, rate: 0.3, cumulative: 2_580 },
    { threshold: 36_000, rate: 0.35, cumulative: 6_180 },
    { threshold: Infinity, rate: 0.4, cumulative: 10_380 },
  ],
  payrollLevies: [
    {
      name: 'NSSA',
      rate: 0.09,
      employeeShare: 0.045,
      employerShare: 0.045,
      monthlyCeiling: 700,
    },
  ],
  withholding: [
    { category: 'Contracts / consultancy', rate: 0.1 },
    { category: 'Rent', rate: 0.1 },
    { category: 'Dividends (resident)', rate: 0.15 },
  ],
  languages: [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'sn', name: 'Shona', nativeName: 'chiShona' },
  ],
  fiscalYearStartMonth: 1,
  taxAuthority: 'ZIMRA',
  blurb: 'ZIMRA VAT 15.5%, USD, NSSA 9%, AIDS levy 3% on PAYE.',
};

export const REGIONS: Record<RegionCode, RegionConfig> = { BW, ZA, NG, ZW };
export const REGION_CODES: RegionCode[] = ['BW', 'ZA', 'NG', 'ZW'];
export const DEFAULT_REGION: RegionCode = 'BW';

export function getRegion(code: string | null | undefined): RegionConfig {
  if (!code) return REGIONS[DEFAULT_REGION];
  const cfg = REGIONS[code as RegionCode];
  return cfg ?? REGIONS[DEFAULT_REGION];
}
